import {API_BASE_URL} from '../constant/constant';
// Helper function to handle API responses


const handleResponse = async (response) => {
  if (!response.ok) {
    let errorMessage = 'Payment failed';
    let errorDetails = null;
    try {
      const errorData = await response.json();
      errorMessage = errorData.message || errorData.error || errorMessage;
      errorDetails = errorData;
      console.error('Backend error response:', errorData);
    } catch (parseError) {
      // If we can't parse the error response, use a generic message
      errorMessage = `Server error: ${response.status} ${response.statusText}`;
      console.error('Failed to parse error response:', parseError);
    }
    const error = new Error(errorMessage);
    error.details = errorDetails;
    throw error;
  }
  return response.json();
};

// Create payment intent
export const createPaymentIntent = async (plan, token, userEmail = null) => {
  try {
    // Validate plan object
    if (!plan) {
      throw new Error('Plan is required to create payment intent');
    }

    // Support both new Stripe Price ID format and legacy plan name
    const requestBody = {};
    
    if (plan.stripePriceId) {
      requestBody.stripePriceId = plan.stripePriceId;
    } else if (plan.id) {
      // If plan has an id but no stripePriceId, use the id
      requestBody.stripePriceId = plan.id;
    } else if (plan.name) {
      // Fallback to plan name for legacy support
      requestBody.plan = plan.name;
    } else {
      throw new Error('Plan must have either stripePriceId, id, or name');
    }

    // Add user email if provided (required for creating Stripe customer)
    if (userEmail) {
      requestBody.email = userEmail;
    } else {
      console.warn('No user email provided for payment intent creation');
    }

    // Add discount code if provided
    if (plan.discountCode) {
      requestBody.discountCode = plan.discountCode;
    }

    console.log('Creating payment intent with request body:', requestBody);

    const response = await fetch(`${API_BASE_URL}/payments/create-payment-intent`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(requestBody),
    });

    const data = await handleResponse(response);
    
    // Validate response has clientSecret
    if (!data || !data.clientSecret) {
      console.error('Invalid response from payment intent creation:', data);
      throw new Error('Invalid response from payment server. Please try again.');
    }

    return data;
  } catch (error) {
    console.error('Error creating payment intent:', error);
    // Re-throw with more context if it's already an Error with message
    if (error instanceof Error) {
      throw error;
    }
    throw new Error(error.message || 'Failed to create payment. Please check your connection and try again.');
  }
};

// Confirm payment intent or subscription
// Optional 4th argument `discountCode` lets the backend patch a discount
// at confirm time (e.g. if the code was applied after the intent was created).
export const confirmPayment = async (paymentIntentId, token, subscriptionId = null, discountCode = null) => {
  try {
    const body = subscriptionId
      ? { subscriptionId }
      : { paymentIntentId };

    if (discountCode) {
      body.discountCode = discountCode;
    }
    
    const response = await fetch(`${API_BASE_URL}/payments/confirm-payment`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(body),
    });

    return handleResponse(response);
  } catch (error) {
    console.error('Error confirming payment:', error);
    throw new Error('Failed to confirm payment. Please contact support if the charge was successful.');
  }
};

// Get pricing plans from Stripe (public endpoint - no token required)
export const getPublicPricingPlans = async (usePaystack = false) => {
  try {
    // Use Paystack endpoint if requested (for African users)
    const endpoint = usePaystack 
      ? `${API_BASE_URL}/payments/paystack/pricing-plans` 
      : `${API_BASE_URL}/payments/pricing-plans`;
    
    const response = await fetch(endpoint, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      let errorMessage = `Failed to fetch pricing plans: ${response.status} ${response.statusText}`;
      try {
        const errorData = JSON.parse(errorText);
        errorMessage = errorData.message || errorData.error || errorMessage;
      } catch (e) {
        // If parsing fails, use the status text
      }
      throw new Error(errorMessage);
    }

    const data = await response.json();
    // Handle both wrapped (data.plans) and unwrapped (array) responses
    const plans = Array.isArray(data) ? data : (data.plans || data.data || []);
    return plans;
  } catch (error) {
    console.error('Error fetching public pricing plans:', error);
    throw error;
  }
};

// Get pricing plans from Stripe (authenticated endpoint)
export const getPaymentPlans = async (token) => {
  const response = await fetch(`${API_BASE_URL}/payments/pricing-plans`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
  });

  const data = await handleResponse(response);
  return data.plans || data; // Handle both wrapped and unwrapped responses
};

// Refresh pricing plans cache (Admin only)
export const refreshPricingPlans = async (token) => {
  try {
    const response = await fetch(`${API_BASE_URL}/payments/refresh-pricing-plans`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
    });

    return handleResponse(response);
  } catch (error) {
    console.error('Error refreshing pricing plans:', error);
    throw new Error('Failed to refresh pricing plans. Please try again later.');
  }
};

// Save payment method for future use
export const savePaymentMethod = async (paymentMethodId, token) => {
  const response = await fetch(`${API_BASE_URL}/payments/save-payment-method`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify({ paymentMethodId }),
  });

  return handleResponse(response);
};

// Get saved payment methods
export const getSavedPaymentMethods = async (token) => {
  const response = await fetch(`${API_BASE_URL}/payments/saved-methods`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
  });

  return handleResponse(response);
};

// Delete saved payment method
export const deletePaymentMethod = async (paymentMethodId, token) => {
  const response = await fetch(`${API_BASE_URL}/payments/delete-payment-method`, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify({ paymentMethodId }),
  });

  return handleResponse(response);
};

// Get user subscription
export const getSubscription = async (token) => {
  try {
    const response = await fetch(`${API_BASE_URL}/payments/subscription`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
    });

    return handleResponse(response);
  } catch (error) {
    console.error('Error fetching subscription:', error);
    throw new Error('Failed to fetch subscription. Please try again later.');
  }
};

// Request custom plan
export const requestCustomPlan = async (subject, description, token) => {
  try {
    const response = await fetch(`${API_BASE_URL}/payments/request-custom-plan`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({
        subject,
        description
      }),
    });

    return handleResponse(response);
  } catch (error) {
    console.error('Error requesting custom plan:', error);
    throw new Error('Failed to request custom plan. Please try again later.');
  }
};

// Cancel subscription
export const cancelSubscription = async (cancelImmediately = false, token) => {
  try {
    const response = await fetch(`${API_BASE_URL}/payments/subscription/cancel`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({
        cancelImmediately: cancelImmediately
      }),
    });

    return handleResponse(response);
  } catch (error) {
    console.error('Error canceling subscription:', error);
    throw new Error('Failed to cancel subscription. Please try again later.');
  }
};

// Validate discount code
export const validateDiscount = async (code, planId, originalPrice, currency, token) => {
  try {
    const requestBody = {
      code,
      planId,
      originalPrice,
      currency: currency || 'usd'
    };

    const response = await fetch(`${API_BASE_URL}/payments/validate-discount`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(requestBody),
    });

    return handleResponse(response);
  } catch (error) {
    console.error('Error validating discount:', error);
    if (error instanceof Error) {
      throw error;
    }
    throw new Error('Failed to validate discount code. Please check the code and try again.');
  }
};